import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { selectCompanyId } from '../../../../core/store/auth/auth.selectors';
import { SettingsService } from '../../../../core/services/settings.service';
import { filter, take } from 'rxjs';
import { toast } from 'ngx-sonner';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-novigest-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButton,
    PasswordModule,
    ToggleSwitchModule,
    HasPermissionDirective
  ],
  templateUrl: './novigest-tab.html'
})
export class NovigestTab implements OnInit {
  private translate = inject(TranslateService);
  private store = inject(Store);
  private settingsService = inject(SettingsService);
  private router = inject(Router);
  private perm = inject(PermissionService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canEdit(): boolean { return this.perm.canEdit(MODULE.CONFIGURACOES); }

  companyId: number | null = null;
  novigestToken = signal<string>('');
  isSaving = signal<boolean>(false);
  hasSavedToken = signal<boolean>(false);
  autoSync = signal<boolean>(false);
  isSavingAutoSync = signal<boolean>(false);

  ngOnInit() {
    this.store
      .select(selectCompanyId)
      .pipe(
        filter((id) => !!id),
        take(1),
      )
      .subscribe((id) => {
        this.companyId = id;
        this.loadCompanyData();
      });
  }

  loadCompanyData() {
    if (!this.companyId) return;

    this.settingsService.getCompanyData(this.companyId).subscribe({
      next: (data: any) => {
        if (data && data.novigest_token) {
          this.novigestToken.set(data.novigest_token);
          this.hasSavedToken.set(data.novigest_token.trim() !== '');
        } else {
          this.hasSavedToken.set(false);
        }
        this.autoSync.set(!!data?.auto_sync);
      },
      error: (err: any) => {
        console.error('Error loading Novigest token:', err);
      }
    });
  }

  onSave() {
    if (!this.canEdit) return;
    if (!this.companyId) return;

    this.isSaving.set(true);
    
    this.settingsService.updateCompanyData(this.companyId, {
      novigest_token: this.novigestToken()
    }).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.hasSavedToken.set(this.novigestToken().trim() !== '');
        toast.success(this.translate.instant('novigest_save_success'));
      },
      error: (err: any) => {
        this.isSaving.set(false);
        console.error('Error updating Novigest token:', err);
        toast.error(this.translate.instant('novigest_save_error'));
      }
    });
  }

  onImportHistoricData() {
    if (!this.canCreate) return;
    this.router.navigate(['/settings/novigest-import']);
  }

  onAutoSyncChange(checked: boolean) {
    if (!this.canEdit) return;
    if (!this.companyId || !this.hasSavedToken()) return;

    this.isSavingAutoSync.set(true);

    this.settingsService.updateCompanyData(this.companyId, {
      auto_sync: checked
    }).subscribe({
      next: () => {
        this.isSavingAutoSync.set(false);
        toast.success(this.translate.instant('novigest_auto_sync_success'));
      },
      error: (err: any) => {
        this.isSavingAutoSync.set(false);
        this.autoSync.set(!checked);
        console.error('Error updating Novigest auto sync:', err);
        toast.error(this.translate.instant('novigest_auto_sync_error'));
      }
    });
  }
}
